import React, {
  useState,
  useRef,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import styles from "./CustomSelect.module.css";
import { Check, ChervonDown } from "../../../assets/icons";

export interface OptionType {
  label: string;
  value: string;
}

interface CustomSelectProps {
  value?: string;
  onChange?: (val: string | undefined) => void;
  placeholder?: string;
  options: OptionType[];
  disabled?: boolean;
  className?: string;
}

const CustomSelect: React.FC<CustomSelectProps> = ({
  value,
  onChange,
  placeholder = "Выберите значение",
  options,
  disabled,
  className,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropUp, setDropUp] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const checkDropdownPosition = useCallback(() => {
    if (!containerRef.current) return;

    const rect = containerRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const dropdownHeight = 200;
    const margin = 20;

    setDropUp(spaceBelow < dropdownHeight + margin);
  }, []);

  const handleSelectOption = useCallback(
    (optionValue: string) => {
      onChange?.(optionValue === value ? undefined : optionValue);
      setIsOpen(false);
    },
    [value, onChange],
  );

  const handleToggleDropdown = useCallback(() => {
    if (!disabled) {
      setIsOpen((prev) => !prev);
    }
  }, [disabled]);

  const handleClickOutside = useCallback((event: MouseEvent) => {
    if (
      containerRef.current &&
      !containerRef.current.contains(event.target as Node)
    ) {
      setIsOpen(false);
    }
  }, []);

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [handleClickOutside]);

  useEffect(() => {
    if (isOpen) {
      checkDropdownPosition();
    }
  }, [isOpen, checkDropdownPosition]);

  const selectedLabel = useMemo(() => {
    if (!value) return null;
    return options.find((opt) => opt.value === value)?.label;
  }, [value, options]);

  const renderValue = useMemo(() => {
    if (!selectedLabel) {
      return <span className={styles.placeholder}>{placeholder}</span>;
    }

    return <span className={styles.value}>{selectedLabel}</span>;
  }, [selectedLabel, placeholder]);

  const renderOptions = useMemo(() => {
    if (options.length === 0) {
      return <div className={styles.empty}>Нет данных</div>;
    }

    return options.map((option) => {
      const isSelected = value === option.value;
      return (
        <div
          key={option.value}
          className={styles.optionWrapper}
          onClick={() => !disabled && handleSelectOption(option.value)}
        >
          <div
            className={`${styles.option} ${
              isSelected ? styles.optionSelected : ""
            }`}
          >
            <span className={styles.optionLabel}>{option.label}</span>
            {isSelected && (
              <span className={styles.checkmark}>
                <Check />
              </span>
            )}
          </div>
        </div>
      );
    });
  }, [options, value, disabled, handleSelectOption]);

  const dropdownClassName = `${styles.dropdown} ${dropUp ? styles.dropUp : ""}`;
  const arrowClassName = `${styles.arrow} ${isOpen ? styles.arrowOpen : ""} ${
    disabled ? styles.disabled : ""
  }`;
  const inputBoxClassName = `${styles.inputBox} ${
    disabled ? styles.disabled : ""
  }`;

  return (
    <div className={`${styles.container} ${className}`} ref={containerRef}>
      <div className={inputBoxClassName} onClick={handleToggleDropdown}>
        <div className={styles.valueWrapper}>{renderValue}</div>
        <span className={arrowClassName}>
          <ChervonDown />
        </span>
      </div>
      {isOpen && <div className={dropdownClassName}>{renderOptions}</div>}
    </div>
  );
};

export default CustomSelect;
